import { NavigationContainer } from "@react-navigation/native";
import { getNavigationAnalytics } from "@utils/analytics";
import getLinking from "@utils/deeplink";
import Header from "@components/common/Header";
import MainStack from "@navigations/main-navigator";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AppStateProps } from "@scenes/splash-screen/types";
import InitialSetupScreen from "@scenes/splash-screen/InitialSetupScreen";
import Routes from "@navigations/Routes";
import useInternetConnectionListener from "../hooks/useInternetConnectionListener";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components/native";
import AuthenticationModal from "@scenes/authentication/AuthenticationModal";
import useGuestUser from "@utils/guest-user";
import useFavicon from "../hooks/useFavicon";
import Loader from "@components/atoms/Loader";
import { Platform } from "react-native";
import useInstallationIdentifier from "../hooks/useInstallationIdentifier";
import useCheckUpdates from "../hooks/useCheckUpdates";
import StoreLinksModal from "@components/molecules/StoreLinksModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import useIsBrowserRefresh from "./useRefreshPage";
import { PERSISTENCE_KEY } from "@utils/product/productValidators";
import { useInjectMetaTags } from "../hooks/useInjectScript";
import { useInitialUrl } from "@utils/useInitialUrl";
import {
  AnalyticsProvider,
  createClient,
  SegmentClient,
} from "@segment/analytics-react-native";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useEnvConfig } from "@redux/slices/globalSlice";
import { useGetScreenData } from "../hooks/useGetScreenData";
import { ScreenName } from "@utils/constants";
import { getDefaultScreenConfig } from "@navigations/utils/convertTabsData";
import { getExploreNavigatorInitialRouteName } from "@navigations/utils/getExploreNavigatorInitialRoute";
import useAllowedProductTypes from "@components/common/hooks/useAllowedProductTypes";
import Constants from "expo-constants";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { clearHistory } from "../redux/slices/navigationHistorySlice";
import {
  selectUserIframeStatus,
  useUserInfo,
} from "@redux/slices/userInfoSlice";
import { appConfig } from "@utils/analytics";
import { SegmentClientProvider } from "../contexts/SegmentClientContext";
import { NavigationHistoryState } from "@components/molecules/Breadcrumbs/utils";
import BuilderAlertModal from "@scenes/jewelry-builder/components/BuilderAlertModal";
import useWebConnectionListener from "../hooks/useWebConnectionListener";
import { navigationRef } from "./utils/RootNavigation";
import { initStorage } from "vdb-ai-chat";

const NavigationBase = () => {
  useFavicon();
  if (Platform.OS === "web") {
    useWebConnectionListener();
  } else {
    useInternetConnectionListener();
  }
  if (Platform.OS !== "web") {
    initStorage(AsyncStorage);
  }

  useInstallationIdentifier();
  const { initStatus } = useOrgSettingsSlice();
  const dispatch = useDispatch();
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );
  useInjectMetaTags();
  const queryClient = useQueryClient();
  const { initialUrl } = useInitialUrl();
  const { t } = useTranslation();
  const env = useEnvConfig();
  const theme = useTheme();
  const { user } = useUserInfo();
  const allowedProductTypes = useAllowedProductTypes();
  const hasSingleAllowedProductType = allowedProductTypes.length === 1;
  const userIframeStatus = useSelector(selectUserIframeStatus);
  const iFrameWithMultipleProducts = useSelector(
    (state: {
      globalData?: {
        app_settings?: { iFrame_with_multiple_products?: boolean };
      };
    }) => state.globalData?.app_settings?.iFrame_with_multiple_products,
    shallowEqual
  );
  const tabsData = useGetScreenData(ScreenName.TABS);
  const defaultScreenConfig = getDefaultScreenConfig(tabsData ?? []) ?? {};
  const exploreNavigatorInitialRoute = useMemo(
    () =>
      getExploreNavigatorInitialRouteName({
        userIframeStatus,
        iFrameWithMultipleProducts,
        defaultScreenConfig,
      }),
    [userIframeStatus, iFrameWithMultipleProducts, defaultScreenConfig]
  );
  const linking = getLinking(
    queryClient,
    t,
    theme.isMobile,
    exploreNavigatorInitialRoute,
    theme.isLargeScreen,
    hasSingleAllowedProductType
  );
  const [appStateReady, setAppStateReady] = useState<AppStateProps>(
    AppStateProps.LOADING
  );
  const [initialScreen, setInitialScreen] = useState<Routes>();

  const routeName = useRef("Unknown");
  const { checkFinished } = useGuestUser();
  const [initialState, setInitialState] = useState();
  const isRefresh = useIsBrowserRefresh();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const installationIdentifier = useSelector(
    (state: any) => state.appState?.installationIdentifier
  );
  const [segmentClient, setSegmentClient] = useState<SegmentClient>();
  const storage = Platform.OS === "web" ? sessionStorage : AsyncStorage;

  useEffect(() => {
    if (!segmentClient && initStatus?.name) {
      const isEdge =
        Platform.OS === "web" && typeof navigator !== "undefined"
          ? navigator.userAgent.includes("Edg") ||
            navigator.userAgent.includes("Edge")
          : 0;
      const client = createClient({
        writeKey:
          Platform.OS === "web" &&
          initStatus?.segment_write_key &&
          env?.production?.includes(window.location.hostname)
            ? initStatus?.segment_write_key
            : process.env.EXPO_PUBLIC_SEGMENT_KEY
            ? process.env.EXPO_PUBLIC_SEGMENT_KEY
            : Constants?.expoConfig?.extra?.segmentKey,
        trackAppLifecycleEvents: false,
        flushAt: isEdge ? 1 : 10,
        flushInterval: 10000,
        maxBatchSize: isEdge ? 1 : 10,
      });
      setSegmentClient(client);
    }
  }, [initStatus?.name]);

  useEffect(() => {
    const restoreState = async () => {
      if (Platform.OS === "web") {
        try {
          const savedState = await storage.getItem(PERSISTENCE_KEY);
          if (savedState) {
            setInitialState(JSON.parse(savedState));
          }
        } catch (e) {
          console.error("Sample Failed to load navigation state:", e);
        }
      }
    };

    restoreState();
    if (history.length > 0 && Platform.OS !== "web") {
      dispatch(clearHistory());
    }
  }, []);

  useEffect(() => {
    const segmentUserInfo = segmentClient?.userInfo.get();
    if (
      segmentClient &&
      user?.id &&
      (!segmentUserInfo?.userId ||
        segmentUserInfo.userId?.toString() !== user?.id.toString())
    ) {
      console.warn(
        "Identifying user with Segment as user is not identified yet"
      );
      segmentClient
        ?.identify(`${user?.id}`, {
          name: `${user?.first_name} ${user?.last_name}`,
          email: user?.email,
          country: user?.country,
          ...appConfig,
        })
        .then();
    } else if (!user?.id && segmentUserInfo?.userId) {
      console.warn("Resetting Segment user as user is logged out");
      segmentClient?.reset().then();
    }
  }, [user, segmentClient]);

  useCheckUpdates(!!initialScreen);

  if (!checkFinished && isCustomApp) return null;
  if (!installationIdentifier) return null;

  if (appStateReady === AppStateProps.LOADING || !initialScreen)
    return (
      <>
        {Platform.OS === "web" && <Loader />}
        {Platform.OS === "web" && !theme.isLargeScreen && (
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
          />
        )}
        <InitialSetupScreen
          appStateReady={appStateReady}
          setAppStateReady={setAppStateReady}
          setInitialScreen={setInitialScreen}
        />
      </>
    );

  const handleStateChange = async (state: any) => {
    try {
      if (segmentClient) {
        getNavigationAnalytics(state, routeName, segmentClient);
      }

      await storage.setItem(PERSISTENCE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error("Error persisting navigation state:", error);
    }
  };

  const handleReady = () => {
    if (!isRefresh || !initialState) return;

    // The persisted state can be structurally incompatible with the currently
    // mounted navigator tree — e.g. it points at `auth-navigation`, but the
    // root navigator only registers that screen while unauthenticated (it swaps
    // to the authenticated screen set once `isReady` resolves). Route sets can
    // also drift across releases. Applying such a state throws a "RESET ... was
    // not handled by any navigator" error, so validate against the live root
    // navigator first and fall back to normal linking / initialScreen routing.
    try {
      const currentRootState = navigationRef.current?.getRootState();
      const availableRouteNames = currentRootState?.routeNames ?? [];
      const restoredRootRoutes = (initialState as any)?.routes ?? [];
      const currentRouteName = navigationRef.current?.getCurrentRoute?.()?.name;
      const isCompatible =
        restoredRootRoutes.length > 0 &&
        restoredRootRoutes.every((route: { name: string }) =>
          availableRouteNames.includes(route.name)
        );

      if (isCompatible && currentRouteName !== Routes.ReviewPage) {
        if (Platform.OS === "web") {
          const currentPath = window.location.pathname;
          const authDeepLinkRoutes = [
            "/register",
            "/forgot-password",
            "/update-password",
            "/confirmation",
            "/application-status",
            "/document-upload",
            "/login",
          ];
          const isAuthDeepLink = authDeepLinkRoutes.some((route) =>
            currentPath.includes(route)
          );
          if (isAuthDeepLink) {
            return;
          }
        }
        navigationRef.current?.reset(initialState);
      }
    } catch (error) {
      console.error("Failed to restore navigation state on refresh:", error);
    }
  };

  const content = (
    <NavigationContainer
      ref={navigationRef}
      onReady={handleReady}
      onStateChange={handleStateChange}
      linking={linking}
    >
      <Header hidden={false} />
      <MainStack
        initialScreen={initialScreen}
        initialUrls={initialUrl || undefined}
      />

      <AuthenticationModal />
      <StoreLinksModal />
      <BuilderAlertModal />
    </NavigationContainer>
  );

  return segmentClient ? (
    <SegmentClientProvider client={segmentClient}>
      <AnalyticsProvider client={segmentClient}>{content}</AnalyticsProvider>
    </SegmentClientProvider>
  ) : (
    content
  );
};

export default NavigationBase;
